import { useMutation } from '@apollo/client';
import FormContainer from '@components/FormContainer';
import InputPhotos from '@components/InputPhotos';
import InputVideos from '@components/InputVideos';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { GET_PRO, UPDATE_PRO_BRAND } from '@gql/pros';
import { toUploadFileInput, toUploadFilesInput } from '@modules/brands/utils';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Stack } from '@mui/material';

type UploadAsset = {
    _id?: string;
    url?: string;
    name?: string;
    size?: number;
    mimetype?: string;
};

type BrandFormValues = {
    brandBanner: File | UploadAsset;
    brandLogo: File | UploadAsset;
    brandVideos: Array<File | UploadAsset>;
    brandPhotos: Array<File | UploadAsset>;
};

type Props = {
    pro?: any;
    onSuccess?: (data?: any) => void;
    onError?: (error?: any) => void;
    isEdit?: boolean;
    disabled?: boolean;
};

export const CompanyPhotosCard = ({ pro, onSuccess, onError, disabled }: Props) => {
    const { enqueueSnackbar } = useSnackbar();
    const [formState, setFormState] = useState<'view' | 'edit'>('view');

    const defaultValues = useMemo<BrandFormValues>(
        () => ({
            brandBanner: (pro?.brandBanner as File | UploadAsset) ?? null,
            brandLogo: (pro?.brandLogo as File | UploadAsset) ?? null,
            brandVideos: (pro?.brandVideos as Array<File | UploadAsset>) ?? [],
            brandPhotos: (pro?.brandPhotos as Array<File | UploadAsset>) ?? [],
        }),
        [pro],
    );

    const { control, handleSubmit, reset } = useForm<BrandFormValues>({
        mode: 'onChange',
        defaultValues,
    });

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    const [updateProBrand, { loading }] = useMutation(UPDATE_PRO_BRAND, {
        onCompleted: data => {
            enqueueSnackbar('Photos de la marque modifiées avec succès', {
                variant: 'success',
            });
            onSuccess?.(data?.updateProBrand);
        },
        onError: error => {
            enqueueSnackbar(error.message, {
                variant: 'error',
            });
            onError?.(error);
        },
    });

    const submitHandler = async (values: BrandFormValues) => {
        if (!pro?._id) return;

        await updateProBrand({
            variables: {
                proId: pro._id,
                brandInput: {
                    brandLogo: toUploadFileInput(values.brandLogo),
                    brandBanner: toUploadFileInput(values.brandBanner),
                    brandPhotos: toUploadFilesInput(values.brandPhotos),
                    brandVideos: toUploadFilesInput(values.brandVideos),
                },
            },
            context: {
                fetchOptions: {
                    useUpload: true,
                },
            },
            refetchQueries: [
                {
                    query: GET_PRO,
                    variables: { proId: pro._id },
                },
            ],
            awaitRefetchQueries: true,
        });

        setFormState('view');
    };

    return (
        <FormContainer
            onBack={() => { }}
            formTitle="Nos Galeries"
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            hideEdit={disabled}
            onCancel={() => {
                reset(defaultValues);
            }}
            breadcrumbs={['Mes informations', 'Nos Galeries']}
            onSubmit={handleSubmit(submitHandler)}
        >
            <InputPhotos
                name="brandPhotos"
                label="Photos de la marque"
                control={control}
                viewOnly={formState === 'view'}
                helpText="Formats acceptés: JPG, PNG (Max: 5MB par image, 10 photos max)"
                validation={{
                    maxSizeInMB: 5,
                    maxCount: 10,
                    aspectRatio: 1,
                }}
                existingFiles={pro?.brandPhotos}
            />
        </FormContainer>
    );
};

export const CompanyVideosCard = ({ pro, onSuccess, onError, disabled }: Props) => {
    const { enqueueSnackbar } = useSnackbar();
    const [formState, setFormState] = useState<'view' | 'edit'>('view');
   
    const defaultValues = useMemo<BrandFormValues>(
        () => ({
            brandBanner: (pro?.brandBanner as File | UploadAsset) ?? null,
            brandLogo: (pro?.brandLogo as File | UploadAsset) ?? null,
            brandVideos: (pro?.brandVideos as Array<File | UploadAsset>) ?? [],
            brandPhotos: (pro?.brandPhotos as Array<File | UploadAsset>) ?? [],
        }),
        [pro],
    );

    const { control, handleSubmit, reset } = useForm<BrandFormValues>({
        mode: 'onChange',
        defaultValues,
    });

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    const [updateProBrand, { loading }] = useMutation(UPDATE_PRO_BRAND, {
        onCompleted: data => {
            enqueueSnackbar('Vidéos de la marque modifiées avec succès', {
                variant: 'success',
            });
            onSuccess?.(data?.updateProBrand);
        },
        onError: error => {
            enqueueSnackbar(error.message, {
                variant: 'error',
            });
            onError?.(error);
        },
    });

    const submitHandler = async (values: BrandFormValues) => {
        if (!pro?._id) return;

        await updateProBrand({
            variables: {
                proId: pro._id,
                brandInput: {
                    brandLogo: toUploadFileInput(values.brandLogo),
                    brandBanner: toUploadFileInput(values.brandBanner),
                    brandPhotos: toUploadFilesInput(values.brandPhotos),
                    brandVideos: toUploadFilesInput(values.brandVideos),
                },
            },
            context: {
                fetchOptions: {
                    useUpload: true,
                },
            },
            refetchQueries: [
                {
                    query: GET_PRO,
                    variables: { proId: pro._id },
                },
            ],
            awaitRefetchQueries: true,
        });

        setFormState('view');
    };

    return (
        <FormContainer
            onBack={() => { }}
            formTitle="Vidéo de présentation"
            loading={loading}
            formState={formState}
            setFormState={setFormState}
            hideEdit={disabled}
            onCancel={() => {
                reset(defaultValues);
            }}
            breadcrumbs={['Mes informations', 'Vidéo de présentation']}
            onSubmit={handleSubmit(submitHandler)}
        >
            <InputVideos
                name="brandVideos"
                label="Vidéos de la marque"
                control={control}
                viewOnly={formState === 'view'}
                helpText="Formats acceptés: MP4, WebM, MOV (Max: 50MB par vidéo, 5 vidéos max)"
                validation={{
                    maxSizeInMB: 50,
                    maxDurationSeconds: 300,
                    maxCount: 5,
                    acceptedFormats: ['mp4', 'webm', 'mov'],
                }}
                existingFiles={pro?.brandVideos}
            />
        </FormContainer>
    );
};

const CompanyBrandForm = (props: Props) => {
    return (
        <Stack gap={3}>
            <CompanyPhotosCard {...props} />
            <CompanyVideosCard {...props} />
        </Stack>
    );
};

export default CompanyBrandForm;
